// Edit transaction dialog — owner-only correction of internal notes.
//
// This used to offer payment-method and payment-status selects as well. They
// were never persisted: updateTransaction() wrote only `notes`, so "Cancelled"
// looked like it saved and did nothing. Status is not a free field — it is
// derived from the ledger, and cancelling a sale is `void_transaction`, which
// restores lot stock and records who approved it.

import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useUpdateTransaction } from '@/hooks/useTransactions';
import type { AppTransaction } from '@/types/domain';

export interface EditTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: AppTransaction;
}

export function EditTransactionDialog({
  open,
  onClose,
  transaction,
}: EditTransactionDialogProps) {
  const updateTx = useUpdateTransaction();
  const [notes, setNotes] = useState<string>(transaction.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNotes(transaction.notes ?? '');
    setError(null);
  }, [open, transaction]);

  const handleSave = async () => {
    setError(null);
    const trimmed = notes.trim();
    if (trimmed === (transaction.notes ?? '')) {
      onClose();
      return;
    }
    try {
      await updateTx.mutateAsync({
        id: transaction.id,
        input: { notes: trimmed.length > 0 ? trimmed : null },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan perubahan');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit catatan"
      description={`${transaction.invoiceNumber} — catatan internal, tidak muncul di invoice customer.`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={updateTx.isPending}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={updateTx.isPending}
            leftIcon={<Save size={16} />}
          >
            Simpan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Catatan"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          autoFocus
          placeholder="Contoh: barang dititip ke resepsionis, diambil besok."
        />
        {error && <p role="alert" className="text-caption text-danger">{error}</p>}
        <Input
          label="Total (tidak bisa diubah)"
          value={transaction.grandTotal.toLocaleString('id-ID')}
          readOnly
          disabled
          hint="Item, total, dan status tidak diedit di sini. Salah transaksi → Void; barang kembali → Retur."
        />
      </div>
    </Modal>
  );
}
